import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  NgZone,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { WindowRef } from '@fcom/core/providers/window';

const mutationObserverInitConfig: Record<string, MutationObserverInit> = {
  default: {
    attributes: false,
    childList: true,
    characterData: false,
    subtree: true,
  },
  travelRestrictions: {
    attributes: true,
    childList: true,
    characterData: false,
    subtree: true,
  },
};

/**
 * @class MutationObserverDirective
 * @description Observe DOM mutations according to the Inputted MutationObserver init configuration and emit them to the Output
 *
 * Usage example:
 *   <div
 *     mutationObserverInitConfigKey='chat'
 *     (finMutationObserver)="onDomChange($event)"
 *     #target
 *   ></div>
 */
@Directive({
  selector: '[finMutationObserver]',
  standalone: false,
})
export class MutationObserverDirective implements OnInit, OnDestroy {
  private windowRef = inject(WindowRef);
  private elementRef = inject(ElementRef);
  private platform = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);

  @Input()
  mutationObserverInitConfigKey = 'default';

  @Output()
  finMutationObserver: EventEmitter<MutationRecord> = new EventEmitter();

  @Output()
  finObserverRemoved: EventEmitter<void> = new EventEmitter();

  private changes: MutationObserver;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platform) && this.detectMutationSupport()) {
      this.observeMutations();
    }
  }

  detectMutationSupport(): boolean {
    return 'MutationObserver' in this.windowRef.nativeWindow;
  }

  observeMutations(): void {
    const element: Node = this.elementRef.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      this.changes = new MutationObserver((mutations: MutationRecord[]) => {
        mutations.forEach((mutation: MutationRecord) => this.ngZone.run(() => this.finMutationObserver.emit(mutation)));
      });

      this.changes.observe(element, mutationObserverInitConfig[this.mutationObserverInitConfigKey]);
    });
  }

  ngOnDestroy(): void {
    if (this.changes) {
      this.finObserverRemoved.emit();
      this.changes.disconnect();
    }
  }
}
